// TODO: util로 분리
import { find, go, head, sel } from 'fxjs/es';
import { MpShopProductS } from '../../../../../modules/MpShop/Product/S/Function/module/MpShopProductS';
import { makeMainUrl } from '../../../shared/util/url';
import { UtilS } from '../../../../../modules/Util/S/Function/module/UtilS';
import { Membership } from '../type';
import {
  compact,
  map,
  pipe,
  toArray,
  includes,
  curry,
  isString,
  filter,
  zipWithIndex,
  isNumber,
} from '@fxts/core';
import { MembershipDepUtilS, MembershipUtilS } from '../outbound/share';
import { GoodsTypeId } from '../../Product/type';
import { ProductsMemberships } from '../../../entities/pool/public/products_memberships';
import { BaseProducts } from '../../../entities/pool/public/base_products';
import { Products } from '../../../entities/pool/public/products';
import { Stores } from '../../../entities/pool/public/stores';
import {
  NullableNumberSchema,
  NullableStringToParsedFloatSchema,
  PriceSchema,
} from '../../../shared/schema/number';
import { MembershipProductData, MembershipProductItems } from '../type/product';
import { associateMembershipProductListForStudio } from '../service';
import {
  isDomainNameRequired,
  isGoodsTypeIdRequired,
  isNameRequired,
  isThumbnailRatioRequired,
  isThumbnailsRequired,
} from '../../../shared/util/typeGuard/type_guard';

type ProductExcludedPrice<T extends Pick<Products, 'price' | 'price_en' | 'price_jp'>> = Omit<
  T,
  'price' | 'price_en' | 'price_jp'
>;

export const processProductPrice = curry(
  <T extends Pick<Products, 'price' | 'price_en' | 'price_jp'>>(
    currency: 'kr' | 'en' | 'jp',
    product: T,
  ): ProductExcludedPrice<T> & { price: number } => {
    const { price, price_en, price_jp, ...els } = product;
    if (currency === 'kr') {
      return {
        ...els,
        price: NullableNumberSchema.parse(price),
      };
    }
    if (currency === 'en') {
      return {
        ...els,
        price: NullableStringToParsedFloatSchema.parse(price_en),
      };
    }
    if (currency === 'jp') {
      return {
        ...els,
        price: NullableStringToParsedFloatSchema.parse(price_jp),
      };
    }
    throw new Error(`Unsupported currency: ${currency}`);
  },
);

export const processProduct = (p: {
  id: Products['id'];
  price: number;
  name: NonNullable<Products['name'] | BaseProducts['name']>;
  domain_name: Stores['domain_name'];
  goods_type_id: GoodsTypeId;
  thumbnails: Products['thumbnails'];
  thumbnail_ratio: number;
  base_product_color_face_url: BaseProducts['base_product_color_face_url'];
}): MembershipProductData => {
  const { thumbnail_ratio } = p;
  const marpple_shop_link = makeMainUrl();

  // TODO: 성인인증 로직 추가
  const image_data = MembershipDepUtilS.getProductImageData({
    ...p,
    thumbnail_ratio,
  });

  return {
    product_id: p.id,
    link: (UtilS.unEscaper(`${marpple_shop_link}/${p.domain_name}/products/${p.id}`) as string) ?? '',
    name: UtilS.unEscaper(p.name) as string,
    price: p.price,
    type: MembershipUtilS.getMembershipProductTypeByGoodsTypeId(p.goods_type_id),
    ...image_data,
  };
};
const to = 10;
const of = 5;
const power = Math.pow(to, of);
const toFixed3 = (r) => Math.round(parseFloat((r * 100 * power).toFixed(of))) / power;
const OPTION_WIDTH = 600;
const isOdd = (r) => r % 2 == 1;

export const getProductImageData = (product: {
  thumbnails: Products['thumbnails'];
  base_product_color_face_url: BaseProducts['base_product_color_face_url'];
  thumbnail_ratio: number;
}): {
  image: string;
  image_ratio: number;
} => {
  const { thumbnails, base_product_color_face_url } = product;

  const thumbnail = go(thumbnails, sel('value'), find(sel('is_thumb')), (v) => v || head(v)) || {};
  const thumbnail_ratio = thumbnail.composite_template_id ? 1 : product.thumbnail_ratio;
  const ratio_size = thumbnail_ratio ? Number.parseFloat(String(thumbnail_ratio)) * 100 : 84;
  const is_100 = ratio_size == 100;
  const width = is_100
    ? OPTION_WIDTH
    : (() => {
        const val = Math.round((OPTION_WIDTH * ratio_size) / 100);
        return val + (isOdd(OPTION_WIDTH) == isOdd(val) ? 0 : 1);
      })();
  const max_size = (is_100 ? ratio_size : toFixed3(width / OPTION_WIDTH)) || 100;

  const url = thumbnail.url || base_product_color_face_url;
  const padding_size = (OPTION_WIDTH - width) / 2 || void 0;
  const bg = 'f6f6f6';

  const url_data: {
    url: string;
    width: number | undefined;
    padding_size: number | undefined;
    quality: number;
    bg: string;
    r?: number;
  } = {
    url,
    width: width || void 0,
    padding_size,
    quality: 92,
    bg,
  };

  /* @@thumbnail is_pb 체크 삭제할 친구(사이어티 행사용) @@ */
  if (thumbnail.is_pb && thumbnail.width != thumbnail.height) {
    url_data.r = 80;
  }

  return {
    image: MpShopProductS.setOnDemandJpegUrl(url_data),
    image_ratio: max_size,
  };
};

export const processMemberships = curry(
  <M extends Membership>(selected_membership_ids: Iterable<string>, memberships: Iterable<M>) => {
    return pipe(
      memberships,
      map((membership) => {
        return {
          ...membership,
          selected: includes(membership.id, selected_membership_ids),
        };
      }),
      compact,
    );
  },
);

/**
 * `products_memberships` 테이블의 데이터로 변환
 */
export const processProductsMembershipsData = ({
  product_id,
  membership_ids,
}: {
  product_id: number;
  membership_ids: Iterable<string>;
}): Omit<ProductsMemberships, 'created_at'>[] => {
  return pipe(
    membership_ids,
    map((membership_id) => ({ membership_id, product_id })),
    toArray,
  );
};

/**
 * `JSON.parse(input)`의 결과가 배열인지 확인하는 함수
 * 파싱 과정에서 에러가 나면 false return
 */
const isStringifiedArray = (input: string): boolean => {
  try {
    const parsed = JSON.parse(input);
    return Array.isArray(parsed);
  } catch (e) {
    return false; // JSON.parse가 실패하면 배열이 아님
  }
};

/**
 * req.body에서 `membership_ids` 뽑아오는 함수
 * (현재는 스튜디오에서 사용함)
 *
 * membership_ids 요청할 때 `JSON.stringify(selected_membership_ids)` 문자열로 변경해서 주기 때문에 parse 하는 과정을 거친다.
 */
export const parseStringifiedMembershipIds = (membership_ids: unknown): string[] | null => {
  // string인 경우 JSON.parse 필요
  if (isString(membership_ids) && isStringifiedArray(membership_ids)) {
    return JSON.parse(membership_ids) as string[];
  }
  return null;
};

export const makeMembershipProductListApiItemsResponse = (
  membership_ids: string[],
  membership_id_products_map: {
    [membership_id: string]: ReturnType<typeof processAssociatedProducts>;
  },
): MembershipProductItems[] => {
  return pipe(
    membership_ids,
    filter((id) => membership_id_products_map[id]?.length),
    zipWithIndex,
    map(([membership_index, membership_id]) => {
      const products = membership_id_products_map[membership_id] ?? [];

      return {
        membership_id,
        index: membership_index,
        products: pipe(
          products,
          map((p) => {
            return processProduct({
              ...p,
              thumbnail_ratio: isNumber(p.thumbnail_ratio)
                ? p.thumbnail_ratio
                : NullableStringToParsedFloatSchema.parse(p.thumbnail_ratio),
            });
          }),
          zipWithIndex,
          map(([product_index, p]) => ({ ...p, index: product_index, parent_index: membership_index })),
          toArray,
        ),
      };
    }),
    filter((m) => m.products.length),
    toArray,
  );
};

export const processAssociatedProducts = (
  products: Awaited<ReturnType<typeof associateMembershipProductListForStudio>>,
) => {
  return pipe(
    products,
    filter(isNameRequired),
    filter(isDomainNameRequired),
    filter(isThumbnailsRequired),
    filter(isThumbnailRatioRequired),
    filter(isGoodsTypeIdRequired),
    map((p) => {
      const { domain_name, price, price_en, price_jp, ...els } = p;
      const price_intl = PriceSchema.parse({
        price,
        price_en,
        price_jp,
      });

      return {
        ...els,
        membership_id: p.membership_id,
        domain_name,
        ...price_intl,
      };
    }),
    toArray,
  );
};
